import { FC, useMemo, useRef, useState } from "react";
import { Box, Flex, Text, Icon } from "@chakra-ui/react";
import { GoogleMap, HeatmapLayer } from "@react-google-maps/api";
import { IconMapPinOff } from "@tabler/icons-react";
import { useGoogleMaps } from "@Hooks/useGoogleMaps";
import { HeatPoint } from "@Api/estudo";

/**
 * Mapa de calor dos abastecimentos (Mapa de Rota).
 *
 * Copiado de rf-gestaofrota-cliente-web
 *   src/Pages/Vehicles/View/Components/Details/Components/VehicleMap/index.tsx
 * (rota /vehicles/{id}/details). Mantidos: mapContainerStyle, options do GoogleMap,
 * center por média + fitBounds, e a configuração do HeatmapLayer (radius/opacity/
 * gradient). REMOVIDOS: AdvancedMarkerElement, MarkerClusterer e InfoWindow —
 * aqui renderizamos APENAS a camada de calor, sem pins/marcadores de posto.
 */

const mapContainerStyle = {
  width: "100%",
  height: "340px",
  borderRadius: "6px",
};

// Gradiente idêntico ao do VehicleMap (azul → verde → amarelo → laranja → vermelho).
const HEATMAP_GRADIENT = [
  "rgba(0, 255, 255, 0)",
  "rgba(0, 191, 255, 0.8)",
  "rgba(0, 127, 255, 1)",
  "rgba(0, 255, 127, 1)",
  "rgba(0, 255, 0, 1)",
  "rgba(127, 255, 0, 1)",
  "rgba(255, 255, 0, 1)",
  "rgba(255, 127, 0, 1)",
  "rgba(255, 0, 0, 1)",
];

interface RotaHeatmapProps {
  points: HeatPoint[];
}

// Bounding box aproximado do território brasileiro. Pontos fora daqui (ex.: (0,0)
// no golfo da Guiné — coordenada "lixo" que passa no Number.isFinite) puxavam o
// fitBounds do Brasil até a África e zeravam o zoom: o mapa aparecia mostrando o
// mundo inteiro com o calor minúsculo. Filtrando aqui, o mapa enquadra só a frota.
const BR_LAT_MIN = -34;
const BR_LAT_MAX = 6;
const BR_LON_MIN = -74;
const BR_LON_MAX = -34;
const isBrazilGeo = (lat: number, lon: number) =>
  Number.isFinite(lat) &&
  Number.isFinite(lon) &&
  lat >= BR_LAT_MIN &&
  lat <= BR_LAT_MAX &&
  lon >= BR_LON_MIN &&
  lon <= BR_LON_MAX;

const EmptyMap: FC<{ message: string }> = ({ message }) => (
  <Flex
    direction="column"
    align="center"
    justify="center"
    h="340px"
    gap={2}
    bg="neutrals.background.weak-50"
    borderRadius="6px"
    border="1px solid"
    borderColor="neutrals.stroke.soft-200"
  >
    <Icon as={IconMapPinOff} boxSize={6} color="neutrals.icon.soft-400" />
    <Text fontSize="sm" color="neutrals.text.soft-400" fontFamily="body">
      {message}
    </Text>
  </Flex>
);

export const RotaHeatmap: FC<RotaHeatmapProps> = ({ points }) => {
  const { isLoaded, loadError } = useGoogleMaps();
  const mapRef = useRef<google.maps.Map | null>(null);
  const [mapReady, setMapReady] = useState(false);

  // HeatPoint usa `lon`; o Google Maps usa `lng`. Peso = litros do ponto.
  // Só pontos dentro do Brasil entram — evita que uma coordenada lixo estoure o zoom.
  const validPoints = useMemo(
    () => points.filter((p) => isBrazilGeo(p.lat, p.lon)),
    [points]
  );

  const center = useMemo(() => {
    if (validPoints.length > 0) {
      const avgLat =
        validPoints.reduce((sum, p) => sum + p.lat, 0) / validPoints.length;
      const avgLng =
        validPoints.reduce((sum, p) => sum + p.lon, 0) / validPoints.length;
      return { lat: avgLat, lng: avgLng };
    }
    return { lat: -15.7801, lng: -47.9292 };
  }, [validPoints]);

  const heatmapData = useMemo(() => {
    if (!mapReady || !window.google?.maps?.visualization) return [];
    return validPoints.map((p) => ({
      location: new window.google.maps.LatLng(p.lat, p.lon),
      weight: p.liters > 0 ? p.liters : 1,
    }));
  }, [validPoints, mapReady]);

  if (loadError) {
    return <EmptyMap message="Não foi possível carregar o mapa." />;
  }
  if (validPoints.length === 0) {
    return <EmptyMap message="Sem pontos de abastecimento geolocalizados." />;
  }
  if (!isLoaded) {
    return <EmptyMap message="Carregando mapa…" />;
  }

  return (
    <Box>
      <GoogleMap
        mapContainerStyle={mapContainerStyle}
        center={center}
        zoom={5}
        onLoad={(map) => {
          mapRef.current = map;
          const bounds = new window.google.maps.LatLngBounds();
          validPoints.forEach((p) => bounds.extend({ lat: p.lat, lng: p.lon }));
          map.fitBounds(bounds, { top: 50, right: 50, bottom: 50, left: 50 });
          // Posto único (ou todos muito próximos) → bounds sem área → fitBounds vai
          // ao zoom máximo (visão de rua). Limita o zoom após o primeiro ajuste.
          window.google.maps.event.addListenerOnce(map, "idle", () => {
            const z = map.getZoom();
            if (typeof z === "number" && z > 12) map.setZoom(12);
          });
          setMapReady(true);
        }}
        options={{
          zoomControl: true,
          streetViewControl: false,
          mapTypeControl: false,
          fullscreenControl: true,
          minZoom: 3,
          maxZoom: 20,
        }}
      >
        {heatmapData.length > 0 && (
          <HeatmapLayer
            data={heatmapData}
            options={{
              radius: 40,
              opacity: 0.6,
              gradient: HEATMAP_GRADIENT,
            }}
          />
        )}
      </GoogleMap>
    </Box>
  );
};
